/**
 * Architecture Health Service
 *
 * Persists health check runs and violation records to Supabase.
 * Fails gracefully — all errors are logged but never thrown,
 * so the panel works in-memory even if the DB tables don't exist yet.
 *
 * Required tables:
 *   architecture_health_runs   — one row per health check execution with pass/fail summary
 *   architecture_violations    — persisted violation records (survive page refresh)
 */

import { supabase } from "@/lib/supabase";
import type { ArchitectureHealthReport } from "@/data/moduleOwnership";
import {
  toDbHealthRun,
  toDbViolation,
  toUiHealthRun,
  toUiViolation,
} from "@/mappers/architecture-health.mapper";

// ── DB row shapes — match the Supabase table columns ─────────────────────────

export interface ArchitectureHealthRunRow {
  id: string;
  ran_at: string;
  healthy: boolean;
  total_checks: number;
  passed_checks: number;
  failed_checks: number;
  violation_count: number;
  created_at: string;
}

export interface ArchitectureViolationRow {
  id: string;
  timestamp: string;
  caller: string;
  domain: string;
  operation: string;
  cleared: boolean;
  created_at: string;
}

// ── UI-facing types ───────────────────────────────────────────────────────────

export interface HealthRunSummary {
  id: string;
  ranAt: string;
  healthy: boolean;
  totalChecks: number;
  passedChecks: number;
  failedChecks: number;
  violationCount: number;
}

export interface PersistedViolation {
  id: string;
  timestamp: string;
  caller: string;
  domain: string;
  operation: string;
  cleared: boolean;
}

// ── Service ───────────────────────────────────────────────────────────────────

export const ArchitectureHealthService = {
  /**
   * Persist a completed health check run summary.
   * Returns the inserted row ID, or null on failure.
   */
  async saveRun(report: ArchitectureHealthReport): Promise<string | null> {
    try {
      const row = toDbHealthRun(report);
      const { data, error } = await supabase
        .from("architecture_health_runs")
        .insert(row)
        .select("id")
        .single();

      if (error) {
        console.warn("[ArchitectureHealthService.saveRun] DB error:", error.message);
        return null;
      }
      return (data as { id: string }).id;
    } catch (err) {
      console.warn("[ArchitectureHealthService.saveRun] Exception:", err);
      return null;
    }
  },

  /**
   * Fetch the N most recent health check run summaries (newest first).
   */
  async getRecentRuns(limit = 10): Promise<HealthRunSummary[]> {
    try {
      const { data, error } = await supabase
        .from("architecture_health_runs")
        .select("*")
        .order("ran_at", { ascending: false })
        .limit(limit);

      if (error) {
        console.warn("[ArchitectureHealthService.getRecentRuns] DB error:", error.message);
        return [];
      }
      return ((data ?? []) as ArchitectureHealthRunRow[]).map(toUiHealthRun);
    } catch (err) {
      console.warn("[ArchitectureHealthService.getRecentRuns] Exception:", err);
      return [];
    }
  },

  /**
   * Persist a batch of violations from the current session.
   * Called after a re-run so violations survive page refresh.
   */
  async saveViolations(
    violations: Array<{ timestamp: string; caller: string; domain: string; operation: string }>,
  ): Promise<void> {
    if (violations.length === 0) return;
    try {
      const rows = violations.map(toDbViolation);
      const { error } = await supabase
        .from("architecture_violations")
        .insert(rows);

      if (error) {
        console.warn("[ArchitectureHealthService.saveViolations] DB error:", error.message);
      }
    } catch (err) {
      console.warn("[ArchitectureHealthService.saveViolations] Exception:", err);
    }
  },

  /**
   * Fetch all uncleared violations from DB.
   * Called on mount to restore violation history across page refreshes.
   */
  async getActiveViolations(): Promise<PersistedViolation[]> {
    try {
      const { data, error } = await supabase
        .from("architecture_violations")
        .select("*")
        .eq("cleared", false)
        .order("timestamp", { ascending: false });

      if (error) {
        console.warn("[ArchitectureHealthService.getActiveViolations] DB error:", error.message);
        return [];
      }
      return ((data ?? []) as ArchitectureViolationRow[]).map(toUiViolation);
    } catch (err) {
      console.warn("[ArchitectureHealthService.getActiveViolations] Exception:", err);
      return [];
    }
  },

  /**
   * Mark all persisted violations as cleared.
   * Called when the user clicks "Clear Violations".
   */
  async clearViolations(): Promise<void> {
    try {
      const { error } = await supabase
        .from("architecture_violations")
        .update({ cleared: true })
        .eq("cleared", false);

      if (error) {
        console.warn("[ArchitectureHealthService.clearViolations] DB error:", error.message);
      }
    } catch (err) {
      console.warn("[ArchitectureHealthService.clearViolations] Exception:", err);
    }
  },
};
